import type { SxProps } from '@mui/material';
import { MenuItem, Divider, Box, styled } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import { ShareNetwork as ShareNetworkIcon } from '@sweep-io/sweep-design/dist/icons/ShareNetwork';
import { Integrations as IntegrationsIcon } from '@sweep-io/sweep-design/dist/icons/Integrations';
import { SignOut as SignOutIcon } from '@sweep-io/sweep-design/dist/icons/SignOut';
import { User as UserIcon } from '@sweep-io/sweep-design/dist/icons/User';
import { Chat as ChatIcon } from '@sweep-io/sweep-design/dist/icons/Chat';
import { Calendar as CalendarIcon } from '@sweep-io/sweep-design/dist/icons/Calendar';
import { Notification as NotificationIcon } from '@sweep-io/sweep-design/dist/icons/Notification';
import { ChevronRight } from '@sweep-io/sweep-design/dist/icons/ChevronRight';
import { HelpCenter } from '@sweep-io/sweep-design/dist/icons/HelpCenter';
import { EnrichLeads } from '@sweep-io/sweep-design/dist/icons/EnrichLeads';
import { Plus } from '@sweep-io/sweep-design/dist/icons/Plus';
import { Cloud } from '@sweep-io/sweep-design/dist/icons/Cloud';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import useLogout from '../../../auth/useLogout';
import type { UserInfo } from '../../../types/UserInfoTypes';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { openIntercom } from '../customerSupportHelper';
import type { ReactNode } from 'react';
import WithNotificationDot from '../../common/WithNotificationDot';
import { sharedHoverMenuItemStyles } from '../../../appTheme/appTheme';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { UI_EVENTS } from '../../../services/events';
import useBookDemoClickHandler from '../../../hooks/useBookDemoClickHandler';
import { UserSettingsTab } from '../user-settings/types';
import { appRoutes } from '../../../constants/appRoutes';
import { useNavigate } from 'react-router';
import { useAccountSelectionContext } from '../../account-selection/AccountSelectionContext';

const MenuItemsUser = ({
  closeMenu,
  openNotificationsPopover,
  hasUnreadNotifications,
  isNotificationsPopoverOpen,
  openAccountSelectionDialog,
}: {
  closeMenu: () => void;
  openNotificationsPopover: () => void;
  hasUnreadNotifications: boolean;
  isNotificationsPopoverOpen: boolean;
  openAccountSelectionDialog: () => void;
}) => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const userInfo = useSelector(selectUserInfoData);
  const sendBiEvent = useSendBiEvent();
  const onBookDemoClick = useBookDemoClickHandler();
  const { isSSOUser, accounts } = useAccountSelectionContext();
  const hasManyAccounts = accounts && accounts.length > 1;

  return (
    <>
      <Box sx={{ padding: '4px 0 4px 12px', mb: 0.5, height: '32px' }}>
        <Typography variant="caption" color={colors.grey[700]}>
          {userInfo?.name ?? userInfo?.email}
        </Typography>
      </Box>
      <UserMenuItem
        text="My profile"
        callback={() => {
          closeMenu();
          navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.PROFILE}`);
        }}
        startIcon={<UserIcon size="medium" />}
      />
      <StyledDivider />
      <Box sx={{ padding: '4px 0 4px 12px', mb: 0.5, height: '32px' }}>
        <Typography variant="caption" color={colors.grey[700]}>
          {userInfo?.account?.name || 'Account'}
        </Typography>
      </Box>
      <UserMenuItem
        text="Users"
        callback={() => {
          closeMenu();
          navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.USERS}`);
        }}
        startIcon={<ShareNetworkIcon size="medium" />}
      />
      <UserMenuItem
        text="Integrations"
        callback={() => {
          closeMenu();
          navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.INTEGRATIONS}`);
        }}
        startIcon={<IntegrationsIcon size="medium" />}
      />
      <UserMenuItem
        text="Environments"
        callback={() => {
          closeMenu();
          navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.ENVIRONMENTS}`);
        }}
        startIcon={<Cloud size="medium" />}
      />
      {!isSSOUser &&
        (hasManyAccounts ? (
          <UserMenuItem
            text="Switch account"
            callback={() => {
              closeMenu();
              openAccountSelectionDialog();
            }}
            startIcon={<EnrichLeads size="medium" />}
          />
        ) : (
          <UserMenuItem
            text="Create new Account"
            textColor={colors.blue[500]}
            callback={() => {
              closeMenu();
              navigate(appRoutes.createAccount.route);
            }}
            startIcon={<Plus size="medium" color={colors.blue[500]} />}
          />
        ))}

      <StyledDivider />
      <UserMenuItem
        text="Chat with us"
        callback={() => {
          closeMenu();
          openIntercom();
        }}
        startIcon={<ChatIcon size="medium" />}
      />
      <UserMenuItem
        text="Schedule a call"
        callback={() => {
          onBookDemoClick();
          closeMenu();
        }}
        startIcon={<CalendarIcon size="medium" />}
      />
      <UserMenuItem
        text="Help"
        callback={() => {
          sendBiEvent({ name: UI_EVENTS.openHelpCenter });
          window.open('https://help.sweep.io/', '_blank');
          closeMenu();
        }}
        startIcon={<HelpCenter size="medium" />}
      />
      <StyledDivider />
      <UserMenuItem
        text="Notification center"
        callback={openNotificationsPopover}
        startIcon={
          <WithNotificationDot showDot={hasUnreadNotifications}>
            <NotificationIcon size="medium" />
          </WithNotificationDot>
        }
        endIcon={<ChevronRight size="medium" />}
        MenuItemPropsSx={isNotificationsPopoverOpen ? sharedHoverMenuItemStyles : {}}
      />
      <UserMenuItem
        text="Log Out"
        MenuItemPropsSx={{
          color: colors.blush[600],
          '& svg path': {
            stroke: colors.blush[600],
          },
        }}
        callback={logout}
        startIcon={<SignOutIcon size="medium" />}
      />
    </>
  );
};

const StyledDivider = styled(Divider)({
  borderColor: colors.grey[120],
});

const UserMenuItem = ({
  callback,
  text,
  MenuItemPropsSx,
  startIcon,
  endIcon,
  user,
  textColor,
}: {
  callback?: () => void;
  text: string;
  MenuItemPropsSx?: SxProps;
  startIcon?: ReactNode;
  textColor?: string;
  endIcon?: ReactNode;
  user?: UserInfo;
}) => {
  return (
    <MenuItem
      onClick={callback}
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 0.5, ...MenuItemPropsSx }}
    >
      {user && (
        <AvatarPicture
          avatar={{ emoji: user.emoji, imageUrl: user.imageUrl }}
          clickable={false}
          dimension={28}
          isInactive={getIsUserInactive(user?.status)}
        />
      )}
      {startIcon}
      <Typography variant="body" color={textColor}>
        {text}
      </Typography>
      <Box ml="auto" display="flex" alignItems="center">
        {endIcon}
      </Box>
    </MenuItem>
  );
};

export { MenuItemsUser };
